import { useState, useEffect, useRef } from 'react';
import { X, ChevronLeft, ChevronRight, Search, Menu, Instagram, Facebook, Twitter } from 'lucide-react';

// Custom hook for AOS-like functionality
const useAOS = () => {
  useEffect(() => {
    const aosElements = document.querySelectorAll('[data-aos]');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('aos-animate');
        }
      });
    }, { threshold: 0.1 });

    aosElements.forEach(element => {
      observer.observe(element);
    });

    return () => {
      aosElements.forEach(element => {
        observer.unobserve(element);
      });
    };
  }, []);
};

const PremiumGallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeFilter, setActiveFilter] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef(null);

  useAOS();

  // Gallery categories for filtering
  const categories = [
    { id: 'all', name: 'All' },
    { id: 'rooms', name: 'Rooms' },
    { id: 'dining', name: 'Dining' },
    { id: 'amenities', name: 'Amenities' },
    { id: 'exterior', name: 'Exterior' }
  ];

  // Gallery images array with categories
  const galleryImages = [
    {
      src: "https://ik.imagekit.io/tdlebsr5e/0N2A5821.JPG?updatedAt=1747388905177",
      category: "rooms",
      title: "Luxury Suite",
      description: "Experience unparalleled comfort in our premium suites"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/PROPERTY%20SHOOT/02/0N2A6047.JPG",
      category: "dining",
      title: "Fine Dining",
      description: "Exquisite culinary experiences await"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/EVENTS/ROOFTOP%20RECEPTION/WhatsApp%20Image%202025-05-16%20at%2018.06.52%20(1).jpeg?updatedAt=1747404486709",
      category: "exterior",
      title: "Grand Entrance",
      description: "A warm welcome to luxury"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/PROPERTY%20SHOOT/02/0N2A5819.JPG?updatedAt=1747392638563",
      category: "rooms",
      title: "Presidential Suite",
      description: "The epitome of luxury living"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/FINAL%20EDITED%20GULMOHAR/0N2A5944.JPG",
      category: "rooms",
      title: "Executive Room",
      description: "The epitome of luxury living"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/FINAL%20EDITED%20GULMOHAR/WhatsApp%20Image%202025-05-16%20at%2015.46.09.jpeg?updatedAt=1747396675420",
      category: "amenities",
      title: "Infinity Pool",
      description: "Swim at the edge of luxury"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/FINAL%20EDITED%20GULMOHAR/WhatsApp%20Image%202025-05-16%20at%2015.47.57.jpeg?updatedAt=1747396493676",
      category: "dining",
      title: "Sky Lounge",
      description: "Dine under the stars with panoramic views"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/FINAL%20EDITED%20GULMOHAR/0N2A5963.JPG?updatedAt=1747392688461",
      category: "rooms",
      title: "Deluxe Room",
      description: "Perfect blend of comfort and elegance"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/FINAL%20EDITED%20GULMOHAR/0N2A0056.JPG?updatedAt=1747392771094",
      category: "amenities",
      title: "Fitness Center",
      description: "State-of-the-art equipment for your wellness"
    }, 
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/EVENTS/ROOFTOP%20RECEPTION/WhatsApp%20Image%202025-05-16%20at%2018.06.52.jpeg?updatedAt=1747404487131",
      category: "amenities",
      title: "Lobby",
      description: "A grand and welcoming space that sets the tone for your luxurious stay"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/FINAL%20EDITED%20GULMOHAR/WhatsApp%20Image%202025-05-16%20at%2015.50.16.jpeg?updatedAt=1747405328021", // replace with actual Grand Cinemaz image URL if different
      category: "entertainment",
      title: "Grand Cinemaz",
      description: "Experience cinematic excellence in a luxurious setting"
    },
    // {
    //   src: "https://gulmohargrandhotel.com/wp-content/uploads/2019/12/S30_0993-700x466.jpg",
    //   category: "exterior",
    //   title: "Garden View",
    //   description: "Lush greenery surrounds our property"
    // },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/PROPERTY%20SHOOT/02/0N2A6029.JPG",
      category: "dining",
      title: "The Tandoor Flames",
      description: "Start your day with culinary excellence"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/PROPERTY%20SHOOT/02/0N2A5860.JPG",
      category: "rooms",
      title: "Family Suite",
      description: "Family meets luxury"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/PHOTO-2025-05-16-18-57-19.jpg",
      category: "amenities",
      title: "Conference Room",
      description: "Premium venue for your important meetings"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/FINAL%20EDITED%20GULMOHAR/DJI_0228.JPG",
      category: "exterior",
      title: "Night View",
      description: "The hotel comes alive at night"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/PROPERTY%20SHOOT/02/0N2A5868.JPG?updatedAt=1747391982144",
      category: "rooms",
      title: "Premium Room",
      description: "Spacious comfort with premium view"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/EVENTS/ROOFTOP%20RECEPTION/WhatsApp%20Image%202025-05-16%20at%2018.21.57.jpeg?updatedAt=1747404486540",
      category: "dining",
      title: "Bar & Lounge",
      description: "Sophisticated ambiance for evening relaxation"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/PHOTO-2025-05-16-17-55-40.jpg",
      category: "amenities",
      title: "Spa Treatment",
      description: "Indulge in our signature treatments"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/EVENTS/ROOFTOP%20RECEPTION/PHOTO-2025-05-16-17-43-13.jpg?updatedAt=1747397602965",
      category: "exterior",
      title: "Poolside",
      description: "Relax by our elegant swimming pool"
    },
    {
      src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PICTURES/PROPERTY%20SHOOT/01/0N2A9969.JPG",
      category: "dining",
      title: "Private Dining",
      description: "Exclusive dining experience for special occasions"
    },
    {
    src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.11%20(1).jpeg?updatedAt=1749122369938",
    category: "dining",
    title: "KFC",
    description: "Finger Lickin' Good"
  },
  {
    src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/PHOTO-2025-06-05-17-27-21(1).jpg?updatedAt=1749124658758",
    category: "dining",
    title: "Barista",
    description: "Premium Coffee Experience"
  },
  {
    src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.13%20(1).jpeg?updatedAt=1749122369971",
    category: "dining",
    title: "Pizza Hut",
    description: "Make it Great"
  },
  {
    src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.12%20(3).jpeg?updatedAt=1749122369912",
    category: "dining",
    title: "Vango",
    description: "Authentic Indian Flavors"
  },
  {
    src: "https://ik.imagekit.io/tdlebsr5e/GULMOHAR%20SHOOT/WhatsApp%20Image%202025-06-05%20at%2016.33.11%20(2).jpeg?updatedAt=1749122370075", // Replace with actual Food Street image if available
    category: "dining",
    title: "Food Street",
    description: "A variety of flavors in one place"
  }
  ];

  // Simulate loading of images
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Focus search input when search is opened
  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  // Filter images based on category and search term
  const filteredImages = galleryImages.filter(image => {
    const matchesCategory = activeFilter === 'all' || image.category === activeFilter;
    const matchesSearch = !searchTerm ||
      image.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      image.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Open lightbox with selected image
  const openLightbox = (image, index) => {
    setSelectedImage(image.src);
    setCurrentIndex(index);
  };

  // Close lightbox
  const closeLightbox = () => {
    setSelectedImage(null);
  };

  // Navigate to previous image
  const prevImage = () => {
    const newIndex = (currentIndex - 1 + filteredImages.length) % filteredImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex].src);
  };

  // Navigate to next image
  const nextImage = () => {
    const newIndex = (currentIndex + 1) % filteredImages.length;
    setCurrentIndex(newIndex);
    setSelectedImage(filteredImages[newIndex].src);
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedImage) return;

      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedImage, currentIndex]);

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Toggle search bar
  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    if (isSearchOpen) {
      setSearchTerm('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white font-sans">
      {/* Loading Screen */}
      {/* {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black">
          <div className="text-center">
            <img 
              src="https://ik.imagekit.io/tdlebsr5e/TheGulmoharGrand.jpeg?updatedAt=1750152666297" 
              alt="Gulmohar Grand Hotel" 
              className="h-32 mx-auto mb-8 animate-pulse"
            />
            <div className="relative w-64 h-1 mx-auto bg-gray-800 overflow-hidden rounded-full">
              <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-amber-400 to-amber-600 animate-slide rounded-full" style={{width: '30%'}}></div>
            </div>
            <p className="mt-6 text-amber-400 font-serif text-lg tracking-wider">GULMOHAR GRAND</p>
          </div>
        </div>
      )} */}

      {/* Hero Section */}
      <div className="relative h-[620px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: `url(https://ik.imagekit.io/tdlebsr5e/PHOTO-2025-05-17-17-47-19.jpg)`,
            transform: "scale(1.1)",
            filter: "brightness(0.6)"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black via-gray-900/20 to-transparent"></div>
        </div>
        <div className="container mx-auto px-4 h-full flex flex-col justify-center items-start relative">
          <h2
            className="text-4xl md:text-6xl font-serif font-bold mb-4 text-center text-white"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <span className="text-amber-400">Luxury</span> Gallery
          </h2>
          {/* <p 
            className="text-lg md:text-xl text-center max-w-2xl text-gray-200"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            Experience the elegance and opulence of Gulmohar Grand Hotel through our carefully curated visual journey
          </p> */}

          <div
            className="mt-8"
            data-aos="fade-up"
            data-aos-delay="300"
          >
            <a
              href="#gallery"
              className="px-8 py-3 bg-amber-400 text-gray-900 font-semibold rounded-full hover:bg-amber-300 transition-colors duration-300 shadow-lg shadow-amber-500/20"
            >
              Explore Gallery
            </a>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="sticky top-16 z-30 bg-gray-900 bg-opacity-90 backdrop-blur-md border-y border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-2 md:space-x-4 overflow-x-auto scrollbar-hide pb-1">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`px-4 py-2 whitespace-nowrap rounded-full transition-all duration-300 ${activeFilter === category.id
                    ? 'bg-amber-400 text-gray-900 font-semibold shadow-lg shadow-amber-500/20'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Gallery Section */}
      <div id="gallery" className="container mx-auto px-4 py-16">
        {filteredImages.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {filteredImages.map((image, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-xl shadow-lg cursor-pointer transform transition-all duration-500 hover:shadow-xl"
                onClick={() => openLightbox(image, index)}
                data-aos="fade-up"
                data-aos-delay={100 + (index % 8) * 50}
                data-aos-duration="800"
              >
                <div className="aspect-[3/2] overflow-hidden">
                  <img
                    src={image.src}
                    alt={image.title}
                    className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                  <h3 className="text-xl font-semibold text-amber-400">{image.title}</h3>
                  <p className="text-sm text-gray-300">{image.description}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-2xl font-semibold text-amber-400 mb-2">No Images Found</h3>
            <p className="text-gray-400">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => { setActiveFilter('all'); setSearchTerm(''); }}
              className="mt-6 px-6 py-2 bg-amber-400 text-gray-900 rounded-full hover:bg-amber-300 transition-colors"
            >
              Reset Filters
            </button>
          </div>
        )}
      </div>

      {/* Gallery Stats */}
      <div className="bg-gray-900 py-16 border-t border-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div
              className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl shadow-lg"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="text-3xl font-bold text-amber-400 mb-2">{galleryImages.length}</div>
              <div className="text-gray-400">Premium Photos</div>
            </div>

            <div
              className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl shadow-lg"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="text-3xl font-bold text-amber-400 mb-2">4</div>
              <div className="text-gray-400">Categories</div>
            </div>

            <div
              className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl shadow-lg"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="text-3xl font-bold text-amber-400 mb-2">5+</div>
              <div className="text-gray-400">Years of Excellence</div>
            </div>

            <div
              className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl shadow-lg"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              <div className="text-3xl font-bold text-amber-400 mb-2">1000+</div>
              <div className="text-gray-400">Happy Guests</div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex justify-center items-center p-4">
          <button
            className="absolute top-4 right-4 text-white p-2 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
            onClick={closeLightbox}
          >
            <X size={24} />
          </button>

          <button
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white p-4 rounded-full bg-gray-800 hover:bg-amber-400 hover:text-gray-900 transition-all duration-300"
            onClick={prevImage}
          >
            <ChevronLeft size={24} />
          </button>

          <div className="relative max-h-[85vh] max-w-[85vw]">
            <img
              src={selectedImage}
              alt="Selected gallery image"
              className="max-h-[85vh] max-w-[85vw] object-contain rounded-lg shadow-2xl"
            />

            <div className="absolute inset-x-0 bottom-0 transform translate-y-full pt-4">
              <div className="bg-gray-900 rounded-lg p-4 mx-auto max-w-md text-center">
                <h3 className="text-xl font-semibold text-amber-400 mb-1">
                  {filteredImages[currentIndex].title}
                </h3>
                <p className="text-gray-300">
                  {filteredImages[currentIndex].description}
                </p>
              </div>
            </div>
          </div>

          <button
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white p-4 rounded-full bg-gray-800 hover:bg-amber-400 hover:text-gray-900 transition-all duration-300"
            onClick={nextImage}
          >
            <ChevronRight size={24} />
          </button>

          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white bg-gray-800 px-6 py-2 rounded-full font-medium">
            {currentIndex + 1} / {filteredImages.length}
          </div>
        </div>
      )}

      <style jsx>{`
        [data-aos] {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s, transform 0.8s;
        }
        
        [data-aos].aos-animate {
          opacity: 1;
          transform: translateY(0);
        }
        
        .animate-kenburns {
          animation: kenburns 15s ease-in-out infinite alternate;
        }
        
        @keyframes kenburns {
          from {
            transform: scale(1) translate(0, 0);
          }
          to {
            transform: scale(1.1) translate(-1%, -1%);
          }
        }
        
        .animate-slide {
          animation: slide 1.5s infinite linear;
        }
        
        @keyframes slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }
        
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }`}
      </style>
    </div>
  )
}
export default PremiumGallery